import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Trophy, Download, ArrowUpDown, KeyRound } from "lucide-react";

const COLS = [
  { key: "executive", label: "Ejecutivo", align: "left" },
  { key: "leads", label: "Leads", align: "right" },
  { key: "visits", label: "Visitas salón", short: "Vis.", align: "right" },
  { key: "test_drives", label: "Test drives", short: "TD", align: "right" },
  { key: "td_rate", label: "% TD / visitas", short: "%TD", align: "right" },
  { key: "sales", label: "Ventas", align: "right" },
  { key: "close_rate", label: "% Cierre", short: "%", align: "right" },
  { key: "avg_days", label: "Días prom.", short: "Días", align: "right" },
];
const MEDAL = ["#d97706", "#94a3b8", "#b45309"];

const downloadCsv = (rows) => {
  const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [["Ejecutivo", "Leads asignados", "Visitas al salón", "Test drives", "% TD sobre visitas", "Ventas", "% Cierre", "Días promedio hasta venta"],
    ...rows.map((r) => [r.executive, r.leads, r.visits, r.test_drives, r.td_rate ?? "", r.sales, r.close_rate, r.avg_days ?? ""])].map((r) => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `tasa_cierre_test_drive_${new Date().toISOString().slice(0, 10)}.csv` });
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

const TdRate = ({ v }) => (v == null ? <span className="text-gray-400">—</span> : (
  <span className={v > 100 ? "text-amber-600" : v >= 30 ? "text-emerald-700" : v >= 15 ? "text-amber-600" : "text-red-600"}
        title={v > 100 ? "Más test drives que visitas registradas: hay test drives de clientes que no entraron como Walk In" : undefined}>{v}%</span>
));

const RateBar = ({ v }) => (
  <div className="flex items-center justify-end gap-2">
    <div className="hidden sm:block w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, v)}%`, background: v >= 30 ? "#16a34a" : v >= 15 ? "#f59e0b" : "#ef4444", transition: "width 400ms ease" }}/>
    </div>
    <span className="w-12 text-right">{v}%</span>
  </div>
);

export default function TestDriveClosingTable({ filters }) {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState({ key: "sales", dir: -1 });
  const [showAll, setShowAll] = useState(false);

  const fkey = JSON.stringify(filters);
  useEffect(() => {
    setData(null);
    api.get("/sales-performance/test-drive-closing", { params: JSON.parse(fkey) })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: {} }));
  }, [fkey]);

  const ranked = useMemo(() => {
    const rows = [...(data?.rows || [])];
    const byRank = [...rows].sort((a, b) => b.sales - a.sales || b.close_rate - a.close_rate);
    const top3 = new Set(byRank.filter((r) => r.sales > 0).slice(0, 3).map((r) => r.executive));
    rows.sort((a, b) => {
      const x = a[sort.key] ?? Infinity * sort.dir, y = b[sort.key] ?? Infinity * sort.dir;
      return (typeof x === "string" ? x.localeCompare(y) : x - y) * sort.dir;
    });
    return rows.map((r) => ({ ...r, medal: top3.has(r.executive) ? byRank.findIndex((b) => b.executive === r.executive) : -1 }));
  }, [data, sort]);

  const visible = showAll ? ranked : ranked.slice(0, 10);
  const t = data?.totals || {};

  return (
    <section className="surface p-5 sm:p-6 mb-6" data-testid="sp-td-closing">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <div className="label-up text-[11px] flex items-center gap-1.5" style={{ color: "#f97316" }}>
            <KeyRound size={13}/> Test drive → Venta
          </div>
          <h2 className="text-xl font-extrabold font-display">Tasa de cierre por ejecutivo</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Leads asignados (todos los estados), visitas al salón (Walk In / Visita al cliente), % de test drive sobre visitas y test drives facturados en Ventas en o después del TD (cruce por nombre, teléfono o email).
          </p>
          {t.matched_by && (
            <p className="text-[11px] text-gray-400 mt-0.5" data-testid="sp-td-closing-matched-by">
              Ventas encontradas por nombre {t.matched_by.nombre} · teléfono {t.matched_by.telefono} · email {t.matched_by.email}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right" data-testid="sp-td-closing-total">
            <div className="text-2xl font-extrabold font-display">{t.close_rate ?? "—"}%</div>
            <div className="text-[11px] font-bold text-gray-500">{t.sales ?? 0} ventas de {t.test_drives ?? 0} test drives</div>
            {t.td_rate != null && (
              <div className="text-[11px] font-bold text-gray-500" data-testid="sp-td-closing-td-rate">
                {t.td_rate}% test drive sobre {t.visits?.toLocaleString("es-DO")} visitas al salón
              </div>
            )}
            {t.avg_days != null && (
              <div className="text-[11px] font-bold text-gray-500" data-testid="sp-td-closing-avg-days">
                {t.avg_days} días promedio hasta la venta
              </div>
            )}
          </div>
          <button onClick={() => downloadCsv(ranked)} disabled={!ranked.length} data-testid="sp-td-closing-csv"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold text-gray-700 hover:bg-gray-50"
                  style={{ transition: "background-color 150ms ease" }}>
            <Download size={13}/> CSV
          </button>
        </div>
      </div>

      {!data ? (
        <div className="h-32 rounded-lg bg-gray-50 animate-pulse" data-testid="sp-td-closing-loading"/>
      ) : !ranked.length ? (
        <div className="text-sm text-gray-500 py-6 text-center" data-testid="sp-td-closing-empty">Sin test drives para estos filtros.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm" data-testid="sp-td-closing-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-gray-400 border-b">
                {COLS.map((c) => (
                  <th key={c.key} className={`py-2 px-1.5 sm:px-2 font-extrabold cursor-pointer select-none ${c.align === "left" ? "text-left" : "text-right"}`}
                      onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : -1 }))}
                      data-testid={`sp-td-closing-sort-${c.key}`}>
                    <span className="inline-flex items-center gap-1">{c.short ? <><span className="sm:hidden">{c.short}</span><span className="hidden sm:inline">{c.label}</span></> : c.label}<ArrowUpDown size={11} className={sort.key === c.key ? "text-gray-700" : ""}/></span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={r.executive} className="border-b last:border-0 hover:bg-gray-50" style={{ transition: "background-color 120ms ease" }}
                    data-testid={`sp-td-closing-row-${i}`}>
                  <td className="py-2 px-1.5 sm:px-2 font-semibold text-gray-800">
                    <span className="inline-flex items-center gap-1.5">
                      {r.medal >= 0 && <Trophy size={14} style={{ color: MEDAL[r.medal] }} data-testid={`sp-td-closing-medal-${r.medal + 1}`}/>}
                      {r.executive}
                    </span>
                  </td>
                  <td className="py-2 px-1.5 sm:px-2 text-right tabular-nums" data-testid={`sp-td-closing-leads-${i}`}>{r.leads?.toLocaleString("es-DO")}</td>
                  <td className="py-2 px-1.5 sm:px-2 text-right tabular-nums" data-testid={`sp-td-closing-visits-${i}`}>{r.visits}</td>
                  <td className="py-2 px-1.5 sm:px-2 text-right tabular-nums">{r.test_drives}</td>
                  <td className="py-2 px-1.5 sm:px-2 text-right tabular-nums font-bold" data-testid={`sp-td-closing-tdrate-${i}`}><TdRate v={r.td_rate}/></td>
                  <td className="py-2 px-1.5 sm:px-2 text-right tabular-nums font-bold">{r.sales}</td>
                  <td className="py-2 px-1.5 sm:px-2 text-right tabular-nums font-bold"><RateBar v={r.close_rate}/></td>
                  <td className="py-2 px-1.5 sm:px-2 text-right tabular-nums text-gray-600" data-testid={`sp-td-closing-days-${i}`}>
                    {r.avg_days != null ? `${r.avg_days} d` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {ranked.length > 10 && (
            <button onClick={() => setShowAll((v) => !v)} data-testid="sp-td-closing-toggle"
                    className="mt-3 text-xs font-bold text-orange-600 hover:underline">
              {showAll ? "Ver solo top 10" : `Ver los ${ranked.length} ejecutivos`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
